import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Connection, PublicKey, VersionedTransaction } from '@solana/web3.js';
import axios from 'axios';
import AlphaScan from './AlphaScan';
import './MemeCoins.css';

import memeCoinLogo from '../assets/meme-coin.png';

const VITE_ENV = typeof import.meta !== 'undefined' ? import.meta.env : undefined;
const HELIUS_API_KEY = VITE_ENV?.VITE_HELIUS_API_KEY || process.env.REACT_APP_HELIUS_API_KEY || '';
const JUPITER_API_KEY = VITE_ENV?.VITE_JUPITER_API_KEY || process.env.REACT_APP_JUPITER_API_KEY || '';

const MemeCoins = ({ user }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://trading.omniscalping.com';

  // Tab state
  const [activeTab, setActiveTab] = useState('swap');

  // Swap states
  const [inputToken, setInputToken] = useState('USDC');
  const [outputToken, setOutputToken] = useState('SOL');
  const [inputAmount, setInputAmount] = useState('');
  const [outputAmount, setOutputAmount] = useState('');
  const [slippage, setSlippage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [searching, setSearching] = useState(false);
  const [tokenSearchResults, setTokenSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [commissionConfig, setCommissionConfig] = useState(null);
  const [userCredits, setUserCredits] = useState(null);
  const [swapHistory, setSwapHistory] = useState([]);
  const [validationError, setValidationError] = useState('');

  // Ultra Swap and Onboarding states
  const [useUltraSwap, setUseUltraSwap] = useState(true);

  // Wallet management states
  const [userWallets, setUserWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [connectedWallet, setConnectedWallet] = useState(null);
  const [walletConnected, setWalletConnected] = useState(false);

 
  // Price chart states
  const [inputTokenPriceData, setInputTokenPriceData] = useState([]);
  const [outputTokenPriceData, setOutputTokenPriceData] = useState([]);
  const [chartTimeframe, setChartTimeframe] = useState('1d');
  const [expandedChart, setExpandedChart] = useState(null);
  const [chartModalTab, setChartModalTab] = useState('transactions');

  // Solana connection
  const connection = new Connection(
    process.env.REACT_APP_HELIUS_RPC_URL || 'https://api.mainnet-beta.solana.com'
  );

  // Your wallet address for collecting fees
  const OMNI_FEE_WALLET = '4YHey4N4FGy8frzq2pw62JWN6EMdde6PCrcP5eUdLChd';

  // Supported stablecoins
  const STABLECOINS = {
    USDC: {
      symbol: 'USDC',
      name: 'USD Coin',
      mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
      decimals: 6,
      logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png'
    },
    USDT: {
      symbol: 'USDT',
      name: 'Tether USD',
      mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
      decimals: 6,
      logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB/logo.png'
    },
    PYUSD: {
      symbol: 'PYUSD',
      name: 'PayPal USD',
      mint: '2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo',
      decimals: 6,
      logoURI: 'https://assets.coingecko.com/coins/images/31212/small/PYUSD_Logo.png'
    }
  };

  // SOL native token configuration
  const SOL_TOKEN = {
    symbol: 'SOL',
    name: 'Solana',
    mint: 'So11111111111111111111111111111111111111112',
    decimals: 9,
    logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png'
  };

  // Load commission config and user data on mount
  useEffect(() => {
    loadCommissionConfig();
    if (user?.id) {
      loadUserCredits();
      loadSwapHistory();
      loadUserWallets();
    }
  }, [user]);

 

  // Load price charts when tokens are selected
  useEffect(() => {
    if (inputToken && activeTab === 'swap') {
      if (inputToken === 'SOL') {
        loadTokenPriceData(SOL_TOKEN.mint, 'input');
      } else if (STABLECOINS[inputToken]) {
        loadTokenPriceData(STABLECOINS[inputToken].mint, 'input');
      }
    }
    if (outputToken && activeTab === 'swap') {
      if (outputToken === 'SOL') {
        loadTokenPriceData(SOL_TOKEN.mint, 'output');
      } else if (STABLECOINS[outputToken]) {
        loadTokenPriceData(STABLECOINS[outputToken].mint, 'output');
      }
    }
  }, [inputToken, outputToken, chartTimeframe]);


  // Load commission configuration - FIXED
  const loadCommissionConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/meme-coins/commission-config?methodology=meme_coins`);
      const data = await response.json();
      if (data.success) {
        setCommissionConfig(data.config);
      } else {
        // Set default commission if API fails
        setCommissionConfig({ commission_percent: 0.5 });
      }
    } catch (error) {
      console.error('Error loading commission config:', error);
      // Set default commission
      setCommissionConfig({ commission_percent: 0.5 });
    }
  };

  // Load user's credit balance - FIXED
  const loadUserCredits = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/meme-coins/user-credits/${user.id}?methodology=meme_coins`);
      const data = await response.json();
      if (data.success) {
        setUserCredits(data.credits);
      } else {
        // Set default credits if API fails
        setUserCredits({ balance_usdc: 0 });
      }
    } catch (error) {
      console.error('Error loading user credits:', error);
      // Set default credits
      setUserCredits({ balance_usdc: 0 });
    }
  };

 // Load swap history
const loadSwapHistory = async () => {
  if (!user?.id) return;
  
  try {
    const response = await fetch(`${API_URL}/api/meme-coins/swap-history/${user.id}?limit=20`);
    const data = await response.json();
    
    if (data.success) {
      setSwapHistory(data.swaps);
    }
  } catch (error) {
    console.error('Error loading swap history:', error);
  }
};

// Connect to browser wallet (Phantom/Solflare)
const connectWallet = async (walletAddress) => {
  try {
    // Check if Phantom or Solflare is installed
    if (!window.solana) {
      alert('Please install Phantom or Solflare wallet extension');
      window.open('https://phantom.app/', '_blank');
      return false;
    }

    // Request connection
    const response = await window.solana.connect();
    const publicKey = response.publicKey.toString();

    console.log('✅ Wallet connected:', publicKey);

    // Verify the connected wallet matches the selected address
    if (walletAddress && publicKey !== walletAddress) {
      alert(`⚠️ Connected wallet (${publicKey.slice(0,8)}...) doesn't match selected wallet (${walletAddress.slice(0,8)}...)\n\nPlease switch to the correct wallet in your browser extension.`);
      await window.solana.disconnect();
      return false;
    }

    setConnectedWallet(publicKey);
    setWalletConnected(true);
    
    // If no wallet selected yet, set this as selected
    if (!selectedWallet) {
      setSelectedWallet(publicKey);
    }

    return true;
  } catch (error) {
    console.error('Error connecting wallet:', error);
    alert('Failed to connect wallet: ' + error.message);
    return false;
  }
};

// Disconnect wallet
const disconnectWallet = async () => {
  try {
    if (window.solana) {
      await window.solana.disconnect();
    }
    setConnectedWallet(null);
    setWalletConnected(false);
    console.log('🔌 Wallet disconnected');
  } catch (error) {
    console.error('Error disconnecting wallet:', error);
  }
};

// Handle wallet selection and auto-connect
const handleWalletSelection = async (walletAddress) => {
  // First disconnect current wallet if any
  if (connectedWallet && connectedWallet !== walletAddress) {
    await disconnectWallet();
  }

  setSelectedWallet(walletAddress);
  
  // Attempt to connect to the browser wallet
  const connected = await connectWallet(walletAddress);
  
  if (connected) {
    setShowWalletModal(false);
  }
};

// Load user's saved wallets
const loadUserWallets = async () => {
  if (!user?.id) return;
  try {
    const response = await fetch(`${API_URL}/api/meme-coins/wallets/${user.id}`);
    const data = await response.json();
    
    console.log('🔍 WALLET API RESPONSE:', data);
    
    if (data.success && data.wallets && Array.isArray(data.wallets)) {
      console.log('✅ Wallets loaded:', data.wallets.length);
      setUserWallets(data.wallets);
      
      // FIX: Use wallet_address and is_primary (snake_case from database)
      const primary = data.wallets.find(w => w.is_primary);
      if (primary && primary.wallet_address) {
        setSelectedWallet(primary.wallet_address);
        // Auto-connect to primary wallet
        connectWallet(primary.wallet_address);
      } else if (data.wallets.length > 0 && data.wallets[0].wallet_address) {
        setSelectedWallet(data.wallets[0].wallet_address);
        // Auto-connect to first wallet
        connectWallet(data.wallets[0].wallet_address);
      }
    } else {
      console.warn('⚠️ No wallets in response or invalid format');
      setUserWallets([]);
    }
  } catch (error) {
    console.error('Error loading wallets:', error);
    setUserWallets([]);
  }
};

// Add wallet
const addWallet = async () => {
  if (!newWalletAddress) return;
  
  // Basic validation for Solana address
  if (newWalletAddress.length < 32 || newWalletAddress.length > 44) {
    alert('Invalid Solana wallet address');
    return;
  }
  
  try {
    const response = await fetch(`${API_URL}/api/meme-coins/wallets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        walletAddress: newWalletAddress,
        isPrimary: userWallets.length === 0
      })
    });
    const data = await response.json();
    if (data.success) {
      setNewWalletAddress('');
      setShowAddWallet(false);
      await loadUserWallets();
      
      // Auto-connect to newly added wallet if it's the first one
      if (userWallets.length === 0) {
        await connectWallet(newWalletAddress);
      }
    } else {
      alert(data.error || 'Failed to add wallet');
    }
  } catch (error) {
    console.error('Error adding wallet:', error);
    alert('Failed to add wallet');
  }
};

// Delete wallet
const deleteWallet = async (walletId) => {
  if (!window.confirm('Delete this wallet?')) return;
  
  // Find the wallet being deleted
  const walletToDelete = userWallets.find(w => w.id === walletId);
  
  try {
    const response = await fetch(`${API_URL}/api/meme-coins/wallets/${walletId}`, {
      method: 'DELETE'
    });
    const data = await response.json();
    if (data.success) {
      // If deleting the connected wallet, disconnect it
      if (walletToDelete && connectedWallet === walletToDelete.wallet_address) {
        await disconnectWallet();
        setSelectedWallet(null);
      }
      loadUserWallets();
    }
  } catch (error) {
    console.error('Error deleting wallet:', error);
  }
};

// Set primary wallet
const setPrimaryWallet = async (walletAddress) => {
  try {
    const response = await fetch(`${API_URL}/api/meme-coins/wallets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        walletAddress: walletAddress,
        isPrimary: true
      })
    });
    const data = await response.json();
    if (data.success) {
      await loadUserWallets();
      setSelectedWallet(walletAddress);
      // Connect to the new primary wallet
      await connectWallet(walletAddress);
    }
  } catch (error) {
    console.error('Error setting primary wallet:', error);
  }
};


 
  // Load token price data for charts - FIXED
  const loadTokenPriceData = async (mintAddress, tokenType) => {
  if (!mintAddress) return;
  
  try {
    const response = await axios.get(`https://api.jup.ag/price/v3?ids=${mintAddress}`, {
      headers: {
        ...(JUPITER_API_KEY ? { 'x-api-key': JUPITER_API_KEY } : {})
      }
    });
    
    const priceData = response.data?.[mintAddress];
    
    if (!priceData || !priceData.usdPrice) {
      console.warn('No price data for', mintAddress);
      const defaultData = Array.from({ length: 50 }, (_, i) => ({
        time: Date.now() - (50 - i) * 3600000,
        price: 1
      }));
      if (tokenType === 'input') {
        setInputTokenPriceData(defaultData);
      } else {
        setOutputTokenPriceData(defaultData);
      }
      return;
    }

    const mockData = Array.from({ length: 50 }, (_, i) => ({
      time: Date.now() - (50 - i) * 3600000,
      price: priceData.usdPrice * (1 + (Math.random() - 0.5) * 0.05)
    }));

    if (tokenType === 'input') {
      setInputTokenPriceData(mockData);
    } else {
      setOutputTokenPriceData(mockData);
    }
  } catch (error) {
    console.error('Error loading price data:', error);
    const defaultData = Array.from({ length: 50 }, (_, i) => ({
      time: Date.now() - (50 - i) * 3600000,
      price: 1
    }));
    if (tokenType === 'input') {
      setInputTokenPriceData(defaultData);
    } else {
      setOutputTokenPriceData(defaultData);
    }
  }
};


  // Search for tokens - FIXED
  const searchTokens = async (query) => {
    if (!query || query.length < 2) {
      setTokenSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get('https://token.jup.ag/strict');
      const tokens = response.data;
      
      if (!Array.isArray(tokens)) {
        console.warn('Token list is not an array:', tokens);
        setTokenSearchResults([]);
        return;
      }
      
      const filtered = tokens
        .filter(token =>
          token.symbol?.toLowerCase().includes(query.toLowerCase()) ||
          token.name?.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 20);
      setTokenSearchResults(filtered);
    } catch (error) {
      console.error('Error searching tokens:', error);
      setTokenSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchTokens(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Validate swap pair - FIXED
  const validateSwapPair = async (inputMint, outputMint) => {
    try {
      const response = await fetch(`${API_URL}/api/meme-coins/validate-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputMint,
          outputMint,
          methodology: 'meme_coins'
        })
      });
      
      if (!response.ok) {
        console.warn('Validation endpoint not available, skipping validation');
        setValidationError('');
        return true; // Allow swap if validation endpoint is down
      }
      
      const data = await response.json();
      if (!data.valid) {
        setValidationError(data.message);
        return false;
      }
      setValidationError('');
      return true;
    } catch (error) {
      console.error('Error validating swap:', error);
      setValidationError('');
      return true; // Allow swap if validation fails
    }
  };


  // Get available output tokens based on selected input token
  const getAvailableOutputTokens = () => {
    if (inputToken === 'SOL') return Object.keys(STABLECOINS);
    else if (Object.keys(STABLECOINS).includes(inputToken)) return ['SOL'];
    return [];
  };

  // Get available input tokens (SOL + all stablecoins)
  const getAvailableInputTokens = () => ['SOL', ...Object.keys(STABLECOINS)];

  // Handle input token change with auto-correction
  const handleInputTokenChange = (newInputToken) => {
    setInputToken(newInputToken);
    if (newInputToken === 'SOL') setOutputToken('USDC');
    else if (Object.keys(STABLECOINS).includes(newInputToken)) setOutputToken('SOL');
    setQuote(null);
    setSearchQuery('');
  };

  // Handle output token change
  const handleOutputTokenChange = (newOutputToken) => {
    setOutputToken(newOutputToken);
    setQuote(null);
  };

  // Get Jupiter Ultra Swap order
  // Get Ultra order
const getUltraOrder = async () => {
  if (!inputAmount || !inputToken || !outputToken || !selectedWallet) return null;

  const inputTokenData = inputToken === 'SOL' ? SOL_TOKEN : STABLECOINS[inputToken];
  const outputTokenData = outputToken === 'SOL' ? SOL_TOKEN : STABLECOINS[outputToken];

  if (!inputTokenData || !outputTokenData) return null;

  try {
    const inputAmountLamports = Math.floor(parseFloat(inputAmount) * Math.pow(10, inputTokenData.decimals));
    const slippageBps = Math.floor(slippage * 100);

    // CORRECT ULTRA API ENDPOINT
    const ultraUrl = `https://api.jup.ag/ultra/v1/order?inputMint=${inputTokenData.mint}&outputMint=${outputTokenData.mint}&amount=${inputAmountLamports}&slippageBps=${slippageBps}&taker=${selectedWallet}`;

    const response = await axios.get(ultraUrl, {
      headers: JUPITER_API_KEY ? { 'x-api-key': JUPITER_API_KEY } : {},
    });

    const orderData = response.data;
    
    // Ultra returns: transaction, requestId, outAmount, priceImpactPct, routePlan
    return {
      ...orderData,
      _ultraTransaction: orderData.transaction,
      _ultraRequestId: orderData.requestId
    };
  } catch (error) {
    console.log('Ultra Swap order failed, will fallback to regular swap:', error);
    return null;
  }
};

// Execute Ultra swap
const executeUltraSwap = async (quote) => {
  if (!quote._ultraTransaction || !quote._ultraRequestId) {
    console.error('Missing Ultra transaction data');
    return false;
  }

  setLoading(true);
  try {
    // Deserialize and sign the transaction
    const transactionBuf = Buffer.from(quote._ultraTransaction, 'base64');
    const transaction = VersionedTransaction.deserialize(transactionBuf);

    if (window.solana && window.solana.signAndSendTransaction) {
      // Sign the transaction
      transaction.sign([/* wallet keypair */]); // Note: Browser wallets handle signing
      const signedTransaction = Buffer.from(transaction.serialize()).toString('base64');

      // Execute via Ultra endpoint
      const executeResponse = await axios.post('https://api.jup.ag/ultra/v1/execute', {
        signedTransaction: signedTransaction,
        requestId: quote._ultraRequestId
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(JUPITER_API_KEY ? { 'x-api-key': JUPITER_API_KEY } : {}),
        }
      });

      const { signature, status } = executeResponse.data;
      console.log('Ultra swap executed:', signature, status);

      // Record the swap (same as regular swap recording)
      const inputUsdValue = parseFloat(inputAmount);
      const outputUsdValue = quote.inputTokenData.symbol === 'USDC' || quote.inputTokenData.symbol === 'USDT' 
        ? parseFloat(inputAmount) 
        : parseFloat(outputAmount);

      const recordResponse = await fetch(`${API_URL}/api/meme-coins/record-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          walletAddress: selectedWallet,
          swapType: Object.keys(STABLECOINS).includes(quote.inputTokenData.symbol) ? 'buy' : 'sell',
          inputMint: quote.inputTokenData.mint,
          inputSymbol: quote.inputTokenData.symbol,
          inputAmount,
          inputDecimals: quote.inputTokenData.decimals,
          inputUsdValue,
          outputMint: quote.outputTokenData.mint,
          outputSymbol: quote.outputTokenData.symbol,
          outputAmount,
          outputDecimals: quote.outputTokenData.decimals,
          outputUsdValue,
          exchangeRate: parseFloat(outputAmount) / parseFloat(inputAmount),
          tokenPriceUsd: quote.outputTokenData.symbol !== 'USDC' && quote.outputTokenData.symbol !== 'USDT' 
            ? outputUsdValue / parseFloat(outputAmount)
            : inputUsdValue / parseFloat(inputAmount),
          jupiterPlatformFee: 0,
          jupiterSwapFee: 0,
          gasFee: 0.000005,
          slippageBps: slippage * 100,
          txSignature: signature,
          quoteId: quote._ultraRequestId,
          routePlan: quote.routePlan,
          methodology: 'meme_coins',
          swapMethod: 'ultra'
        })
      });

      const recordData = await recordResponse.json();
      if (recordData.success) {
        alert(`✅ Ultra Swap successful!\n💰 $${recordData.commissionCollected.toFixed(2)} Earned\n🎁 $${recordData.commissionCredited.toFixed(2)} Applied\n💳 $${recordData.creditApplied.toFixed(2)} Credit\n💵 Balance: $${recordData.newCreditBalance.toFixed(2)}`);
        
        loadUserCredits();
        loadSwapHistory();
        setInputAmount('');
        setOutputAmount('');
        setQuote(null);
      }
      
      setLoading(false);
      return true;
    }
  } catch (error) {
    console.error('Ultra swap execution failed:', error);
    setLoading(false);
    return false;
  }
};



  // Get Jupiter quote (try Ultra first, fallback to regular)

// Get Jupiter quote
const getQuote = async () => {
  if (!inputAmount || !inputToken || !outputToken) return;

  const inputTokenData = inputToken === 'SOL' ? SOL_TOKEN : STABLECOINS[inputToken];
  const outputTokenData = outputToken === 'SOL' ? SOL_TOKEN : STABLECOINS[outputToken];

  if (!inputTokenData || !outputTokenData) {
    alert('Please select valid tokens');
    return;
  }

  const isValidPair = (
    (inputToken === 'SOL' && Object.keys(STABLECOINS).includes(outputToken)) ||
    (Object.keys(STABLECOINS).includes(inputToken) && outputToken === 'SOL')
  );

  if (!isValidPair) {
    setValidationError('Only SOL ↔ Stablecoin swaps are allowed');
    return;
  }

  setValidationError('');
  setLoading(true);

  try {
    const inputAmountLamports = Math.floor(parseFloat(inputAmount) * Math.pow(10, inputTokenData.decimals));
    const platformFeeBps = Math.floor((commissionConfig?.commission_percent || 0.5) * 100);

    const quoteUrl = `https://api.jup.ag/swap/v1/quote?inputMint=${inputTokenData.mint}&outputMint=${outputTokenData.mint}&amount=${inputAmountLamports}&slippageBps=${Math.floor(slippage * 100)}&platformFeeBps=${platformFeeBps}`;

    // ADD API KEY HEADER
    const response = await axios.get(quoteUrl, {
      headers: {
        ...(JUPITER_API_KEY ? { 'x-api-key': JUPITER_API_KEY } : {})
      }
    });

    const quoteData = response.data;

    if (quoteData.error) {
      alert('Quote error: ' + quoteData.error);
      setLoading(false);
      return;
    }

    setQuote({ ...quoteData, inputTokenData, outputTokenData });
    const outputAmountDecimal = parseFloat(quoteData.outAmount) / Math.pow(10, outputTokenData.decimals);
    setOutputAmount(outputAmountDecimal.toFixed(6));
  } catch (error) {
    console.error('Error getting quote:', error);
    alert('Failed to get quote. Please try again.');
  } finally {
    setLoading(false);
  }
};


// Execute swap
const executeSwap = async () => {
  if (!quote || !selectedWallet) {
    alert('Please connect your wallet and get a quote first');
    return;
  }

  setLoading(true);
  try {
    // NEW API ENDPOINT WITH API KEY
    const swapResponse = await axios.post('https://api.jup.ag/swap/v1/swap', {
      quoteResponse: quote,
      userPublicKey: selectedWallet,
      wrapAndUnwrapSol: true,
      feeAccount: OMNI_FEE_WALLET,
      prioritizationFeeLamports: 'auto'
    }, {
      headers: {
        ...(JUPITER_API_KEY ? { 'x-api-key': JUPITER_API_KEY } : {})
      }
    });

    const { swapTransaction } = swapResponse.data;
    const swapTransactionBuf = Buffer.from(swapTransaction, 'base64');
    const transaction = VersionedTransaction.deserialize(swapTransactionBuf);

    if (window.solana && window.solana.signAndSendTransaction) {
      const signature = await window.solana.signAndSendTransaction(transaction);
      console.log('Transaction sent:', signature);

      // Calculate values for recording
      const inputUsdValue = parseFloat(inputAmount);
      const outputUsdValue = quote.inputTokenData.symbol === 'USDC' || quote.inputTokenData.symbol === 'USDT' 
        ? parseFloat(inputAmount) 
        : parseFloat(outputAmount);
      const exchangeRate = parseFloat(outputAmount) / parseFloat(inputAmount);
      const tokenPriceUsd = quote.outputTokenData.symbol !== 'USDC' && quote.outputTokenData.symbol !== 'USDT' 
        ? outputUsdValue / parseFloat(outputAmount)
        : inputUsdValue / parseFloat(inputAmount);

      const inputIsStable = Object.keys(STABLECOINS).includes(quote.inputTokenData.symbol);
      const swapType = inputIsStable ? 'buy' : 'sell';

      // Record swap
      const recordResponse = await fetch(`${API_URL}/api/meme-coins/record-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          walletAddress: selectedWallet,
          swapType,
          inputMint: quote.inputTokenData.mint,
          inputSymbol: quote.inputTokenData.symbol,
          inputAmount,
          inputDecimals: quote.inputTokenData.decimals,
          inputUsdValue,
          outputMint: quote.outputTokenData.mint,
          outputSymbol: quote.outputTokenData.symbol,
          outputAmount,
          outputDecimals: quote.outputTokenData.decimals,
          outputUsdValue,
          exchangeRate,
          tokenPriceUsd,
          jupiterPlatformFee: quote.platformFee || 0,
          jupiterSwapFee: 0,
          gasFee: 0.000005,
          slippageBps: slippage * 100,
          txSignature: signature,
          quoteId: quote.routePlan?.[0]?.swapInfo?.ammKey || null,
          routePlan: quote.routePlan,
          methodology: 'meme_coins',
          swapMethod: 'regular'
        })
      });

      const recordData = await recordResponse.json();
      if (recordData.success) {
        const confirmation = await connection.confirmTransaction(signature, 'confirmed');

        await fetch(`${API_URL}/api/meme-coins/confirm-swap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            swapId: recordData.swapId,
            status: confirmation.value.err ? 'failed' : 'confirmed',
            errorMessage: confirmation.value.err ? JSON.stringify(confirmation.value.err) : null
          })
        });

        alert(`✅ Swap successful!\n💰 $${recordData.commissionCollected.toFixed(2)} Earned\n🎁 $${recordData.commissionCredited.toFixed(2)} Applied\n💳 $${recordData.creditApplied.toFixed(2)} Credit\n💵 Balance: $${recordData.newCreditBalance.toFixed(2)}`);

        loadUserCredits();
        loadSwapHistory();
        setInputAmount('');
        setOutputAmount('');
        setQuote(null);
      }
    } else {
      alert('Please install Phantom or Solflare wallet');
    }
  } catch (error) {
    console.error('Error executing swap:', error);
    alert('Swap failed: ' + error.message);
  } finally {
    setLoading(false);
  }
};


  // Flip tokens
  const flipTokens = () => {
    const tempToken = inputToken;
    const tempAmount = inputAmount;
    setInputToken(outputToken);
    setOutputToken(tempToken);
    setInputAmount(outputAmount);
    setOutputAmount(tempAmount);
    setQuote(null);
  };

 // Open Jupiter onboarding for fiat → SOL
const openOnboarding = () => {
  if (!selectedWallet) { alert('Please connect a wallet first'); return; }
  const onboardUrl = `https://jup.ag/onboard`;
  window.open(onboardUrl, '_blank', 'width=500,height=700,resizable=yes,scrollbars=yes');
};



  // Format percentage with color
  const formatPercentage = (value) => {
    if (!value) return <span className="text-gray">0.00%</span>;
    const isPositive = value >= 0;
    return (
      <span className={isPositive ? 'text-green' : 'text-red'}>
        {isPositive ? '+' : ''}{value.toFixed(2)}%
      </span>
    );
  };

  // ===== CHART DATA TABS COMPONENT =====
    const ChartDataTabs = ({ tokenAddress, tokenSymbol, heliusApiKey }) => {
    const [transactions, setTransactions] = useState([]);
    const [mySwaps, setMySwaps] = useState([]);
    const [holders, setHolders] = useState([]);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [tabLoading, setTabLoading] = useState(false);

    useEffect(() => {
      if (chartModalTab === 'transactions') {
        loadTransactions();
      } else if (chartModalTab === 'mySwaps') {
        loadMySwaps();
      } else if (chartModalTab === 'holders') {
        loadHolders();
      } else if (chartModalTab === 'tokenInfo') {
        loadTokenInfo();
      }
    }, [chartModalTab, tokenAddress]);

    const loadTransactions = async () => {
      setTabLoading(true);
      try {
        const response = await axios.get(
        `https://api.helius.xyz/v0/addresses/${tokenAddress}/transactions?api-key=${heliusApiKey}&limit=50`

        );
        setTransactions(response.data || []);
      } catch (error) {
        console.error('Error loading transactions:', error);
        setTransactions([]);
      } finally {
        setTabLoading(false);
      }
    };

    const loadMySwaps = async () => {
      if (!user?.id) return;
      setTabLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/meme-coins/swap-history/${user.id}?tokenMint=${tokenAddress}`
        );
        const data = await response.json();
        if (data.success) {
          setMySwaps(data.swaps);
        }
      } catch (error) {
        console.error('Error loading my swaps:', error);
        setMySwaps([]);
      } finally {
        setTabLoading(false);
      }
    };

    const loadHolders = async () => {
      setTabLoading(true);
      try {
        const response = await axios.post(
        `https://mainnet.helius-rpc.com/?api-key=${heliusApiKey}`, 
          {
            jsonrpc: '2.0',
            id: 'holder-query',
            method: 'getTokenLargestAccounts',
            params: [tokenAddress]
          }
        );
        setHolders(response.data?.result?.value || []);
      } catch (error) {
        console.error('Error loading holders:', error);
        setHolders([]);
      } finally {
        setTabLoading(false);
      }
    };

    const loadTokenInfo = async () => {
      setTabLoading(true);
      try {
        const response = await axios.get('https://token.jup.ag/strict');
        const token = response.data.find(t => t.address === tokenAddress);
        setTokenInfo(token || null);
      } catch (error) {
        console.error('Error loading token info:', error);
        setTokenInfo(null);
      } finally {
        setTabLoading(false);
      }
    };

    return (
      <div className="chart-data-tabs">
        <div className="chart-tabs-nav">
          <button
            className={chartModalTab === 'transactions' ? 'active' : ''}
            onClick={() => setChartModalTab('transactions')}
          >
            📊 Transactions
          </button>
		  <button
            className={chartModalTab === 'myPositions' ? 'active' : ''}
            onClick={() => setChartModalTab('myPositions')}
          >
            💰 Positions
          </button>
          <button
            className={chartModalTab === 'mySwaps' ? 'active' : ''}
            onClick={() => setChartModalTab('mySwaps')}
          >
            💰 Orders
          </button>
          <button
            className={chartModalTab === 'holders' ? 'active' : ''}
            onClick={() => setChartModalTab('holders')}
          >
            👥 Holders
          </button>
          <button
            className={chartModalTab === 'tokenInfo' ? 'active' : ''}
            onClick={() => setChartModalTab('tokenInfo')}
          >
            ℹ️ DEV Tokens
          </button>
        </div>

        <div className="chart-tabs-content">
          {tabLoading ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <>
              {chartModalTab === 'transactions' && (
                <div className="transactions-list">
                  {transactions.length === 0 ? (
                    <p className="no-data-message">No transactions found</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Signature</th>
                          <th>Type</th>
                          <th>Time</th>
                          <th>Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.slice(0, 20).map((tx, idx) => (
                          <tr key={idx}>
                            <td>
                              <a
                                href={`https://solscan.io/tx/${tx.signature}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tx-link"
                              >
                                {tx.signature?.slice(0, 8)}...
                              </a>
                            </td>
                            <td>{tx.type || 'UNKNOWN'}</td>
                            <td>{new Date(tx.timestamp * 1000).toLocaleString()}</td>
                            <td>{(tx.fee / 1e9).toFixed(6)} SOL</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {chartModalTab === 'mySwaps' && (
                <div className="my-swaps-list">
                  {mySwaps.length === 0 ? (
                    <p className="no-data-message">No swaps for this token yet</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Price</th>
                          <th>Commission</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mySwaps.map((swap, idx) => (
                          <tr key={idx}>
                            <td>{new Date(swap.created_at).toLocaleDateString()}</td>
                            <td>
                              <span className={`swap-type ${swap.swap_type}`}>
                                {swap.swap_type}
                              </span>
                            </td>
                            <td>
                              {parseFloat(swap.output_amount).toFixed(4)} {swap.output_symbol}
                            </td>
                            <td>${parseFloat(swap.token_price_usd).toFixed(6)}</td>
                            <td className="commission-highlight">
                              ${parseFloat(swap.net_omni_commission).toFixed(2)}
                            </td>
                            <td>
                              <span className={`status ${swap.status}`}>
                                {swap.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {chartModalTab === 'holders' && (
                <div className="holders-list">
                  {holders.length === 0 ? (
                    <p className="no-data-message">No holder data available</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Address</th>
                          <th>Amount</th>
                          <th>Decimals</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holders.map((holder, idx) => (
                          <tr key={idx}>
                            <td>#{idx + 1}</td>
                            <td>
                              <a
                                href={`https://solscan.io/account/${holder.address}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tx-link"
                              >
                                {holder.address?.slice(0, 8)}...
                              </a>
                            </td>
                            <td>{(holder.amount / Math.pow(10, holder.decimals)).toFixed(2)}</td>
                            <td>{holder.decimals}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {chartModalTab === 'tokenInfo' && (
                <div className="token-info-details">
                  {!tokenInfo ? (
                    <p className="no-data-message">No token info available</p>
                  ) : (
                    <div className="info-grid">
                      <div className="info-row">
                        <span className="info-label">Name:</span>
                        <span className="info-value">{tokenInfo.name}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Symbol:</span>
                        <span className="info-value">{tokenInfo.symbol}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Mint Address:</span>
                        <span className="info-value mono">
                          <a
                            href={`https://solscan.io/token/${tokenInfo.address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tx-link"
                          >
                            {tokenInfo.address}
                          </a>
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Decimals:</span>
                        <span className="info-value">{tokenInfo.decimals}</span>
                      </div>
                      {tokenInfo.tags && tokenInfo.tags.length > 0 && (
                        <div className="info-row">
                          <span className="info-label">Tags:</span>
                          <span className="info-value">
                            {tokenInfo.tags.map((tag, i) => (
                              <span key={i} className="token-tag">
                                {tag}
                              </span>
                            ))}
                          </span>
                        </div>
                      )}
                      {tokenInfo.logoURI && (
                        <div className="info-row">
                          <span className="info-label">Logo:</span>
                          <span className="info-value">
                            <img src={tokenInfo.logoURI} alt={tokenInfo.symbol} className="token-logo-large" />
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };


  // Mini Chart Component
  const MiniChart = ({ data, color }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
      if (!canvasRef.current || !data || data.length === 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Find min/max for scaling
      const prices = data.map(d => d.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const priceRange = maxPrice - minPrice;

      if (priceRange === 0) return;

      // Draw line
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      data.forEach((point, index) => {
        const x = (index / (data.length - 1)) * width;
        const y = height - ((point.price - minPrice) / priceRange) * height;

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();

      // Fill area under curve
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      ctx.fillStyle = color + '20';
      ctx.fill();

    }, [data, color]);

    return <canvas ref={canvasRef} width={300} height={100} />;
  };

  // Render Swap Tab
  // Render Swap Tab
  const renderSwapTab = () => (
    <div className="swap-tab-container">
      {userCredits && (
        <div className="user-credits-banner">
          <div className="credits-info">
            💳 Available Credits: <span className="credits-amount">${userCredits.balance_usdc?.toFixed(2) || '0.00'}</span>
          </div>
        </div>
      )}

      {validationError && (
        <div className="validation-error">{validationError}</div>
      )}

      <div className="onboarding-banner">
        <div className="onboarding-content">
          <h3>🚀 New to Solana? Get SOL to start trading!</h3>
          <p>Buy SOL directly with your credit card, debit card, or bank transfer</p>
          <button onClick={openOnboarding} className="btn-onboarding">
            💰 Buy SOL with Fiat
          </button>
        </div>
      </div>

      <div className="swap-container">
        <div className="swap-header">
          <h2>SOL ↔ Stablecoin Swap</h2>
          <div className="swap-mode-toggle">
            <label>
              <input type="checkbox" checked={useUltraSwap} onChange={(e) => setUseUltraSwap(e.target.checked)} />
              <span>⚡ Use Ultra Swap (Faster)</span>
            </label>
          </div>
        </div>

        <div className="token-input-section sell-section">
          <div className="section-header">
            <span>Sell</span>
            <span className="balance">0.00 {inputToken}</span>
          </div>
          <div className="token-input-wrapper">
            <select value={inputToken} onChange={(e) => handleInputTokenChange(e.target.value)} className="token-select-button">
              <option value="">Select token...</option>
              {getAvailableInputTokens().map(symbol => (
                <option key={symbol} value={symbol}>
                  {symbol} {symbol === 'SOL' ? '- Solana' : `- ${STABLECOINS[symbol]?.name}`}
                </option>
              ))}
            </select>
            <input type="number" className="amount-input" placeholder="0.00" value={inputAmount}
              onChange={(e) => { setInputAmount(e.target.value); setQuote(null); }} />
          </div>
          <div className="usd-value">$0</div>
        </div>

        <div className="swap-icon-wrapper">
          <button className="swap-icon-button" onClick={flipTokens}>⇅</button>
        </div>

        <div className="token-input-section buy-section">
          <div className="section-header">
            <span>Buy</span>
            <span className="balance">{outputAmount || '0.00'} {outputToken || 'Token'}</span>
          </div>
          <div className="token-input-wrapper">
            <select value={outputToken} onChange={(e) => handleOutputTokenChange(e.target.value)} className="token-select-button">
              <option value="">Select token...</option>
              {getAvailableOutputTokens().map(symbol => (
                <option key={symbol} value={symbol}>
                  {symbol} {symbol === 'SOL' ? '- Solana' : `- ${STABLECOINS[symbol]?.name}`}
                </option>
              ))}
            </select>
            <input type="text" className="amount-input" placeholder="0.00" value={outputAmount} readOnly />
          </div>
          <div className="usd-value">$0</div>
        </div>

        <div className="swap-settings">
          <div className="slippage-setting">
            <label>Slippage: {slippage}%</label>
            <input type="range" min="0.1" max="5" step="0.1" value={slippage}
              onChange={(e) => setSlippage(parseFloat(e.target.value))} className="slippage-slider" />
          </div>
        </div>

        <div className="swap-actions">
          <button onClick={getQuote} disabled={loading || !inputAmount || !inputToken || !outputToken || !selectedWallet} className="btn-get-quote">
            {loading ? 'Loading...' : !selectedWallet ? 'Connect Wallet' : 'Get Quote'}
          </button>
          {quote && (
            <button onClick={executeSwap} disabled={loading} className="btn-execute-swap">
              {loading ? 'Executing...' : quote.isUltra ? '⚡ Execute Ultra Swap' : 'Execute Swap'}
            </button>
          )}
        </div>

        {quote && (
          <div className="quote-details">
            <h4>Quote Details {quote.isUltra && <span className="ultra-badge">⚡ ULTRA</span>}</h4>
            <div className="quote-row">
              <span>Rate</span>
              <span>1 {quote.inputTokenData.symbol} = {(parseFloat(outputAmount) / parseFloat(inputAmount)).toFixed(6)} {quote.outputTokenData.symbol}</span>
            </div>
            <div className="quote-row">
              <span>Price Impact</span>
              <span>
                {quote.priceImpactPct 
                  ? (typeof quote.priceImpactPct === 'number' 
                      ? quote.priceImpactPct.toFixed(2) 
                      : parseFloat(quote.priceImpactPct).toFixed(2)) + '%'
                  : 'N/A'
                }
              </span>
            </div>
            <div className="quote-row">
              <span>Platform Fee</span>
              <span>{commissionConfig?.commission_percent || 0.5}%</span>
            </div>
            <div className="quote-row">
              <span>Minimum Received</span>
              <span>{(parseFloat(outputAmount) * (1 - slippage / 100)).toFixed(6)} {quote.outputTokenData.symbol}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAlphaScanTab = () => {
  return (
    <AlphaScan 
      user={user}
      commissionConfig={commissionConfig}
      selectedWallet={selectedWallet}
      connectedWallet={connectedWallet}
    />
  );
};

  // Render other tabs
  const renderPositionsTab = () => (
    <div className="coming-soon-container">
      <h2>💰 Positions & PnL</h2>
      <p>Track your open positions and realized/unrealized gains.</p>
      <p className="coming-soon-text">Coming Soon</p>
    </div>
  );

  const renderPortfolioTab = () => (
    <div className="coming-soon-container">
      <h2>📊 Portfolio Overview</h2>
      <p>View your complete portfolio breakdown and performance.</p>
      <p className="coming-soon-text">Coming Soon</p>
    </div>
  );

  const renderVideosTab = () => (
    <div className="coming-soon-container">
      <h2>🎥 Tutorial Videos</h2>
      <p>Watch tutorials on how to use the platform.</p>
      <p className="coming-soon-text">Coming Soon</p>
    </div>
  );

  const renderDocsTab = () => (
    <div className="coming-soon-container">
      <h2>📖 Documentation</h2>
      <p>Read comprehensive guides and API documentation.</p>
      <p className="coming-soon-text">Coming Soon</p>
    </div>
  );

  return (
    <div className="meme-coins-container">
      {/* Fixed Header - Shows on all tabs */}
      <div className="meme-coins-header">
        <div className="header-left">
          <img src={memeCoinLogo} alt="Meme Coin Trading" className="omni-logo" />
          <div className="header-title">
            <h1>Meme Coin Trading</h1>
            <p className="powered-by">Powered by Jupiter 🪐</p>
          </div>
        </div>
        <div className="header-right">
          {commissionConfig && (
            <div className="commission-badge">
              Commission: {commissionConfig.commission_percent}%
            </div>
          )}
          <button className="wallets-button" onClick={() => setShowWalletModal(true)}>
            👛 Wallets ({userWallets.length})
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button className={`tab-button ${activeTab === 'swap' ? 'active' : ''}`} onClick={() => setActiveTab('swap')}>
          🏧 Omni
        </button>
        <button className={`tab-button ${activeTab === 'alphascan' ? 'active' : ''}`} onClick={() => setActiveTab('alphascan')}>
          AlphaScan
        </button>
        <button className={`tab-button ${activeTab === 'positions' ? 'active' : ''}`} onClick={() => setActiveTab('positions')}>
          Positions/PnL
        </button>
        <button className={`tab-button ${activeTab === 'portfolio' ? 'active' : ''}`} onClick={() => setActiveTab('portfolio')}>
          Portfolio
        </button>
        <button className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`} onClick={() => setActiveTab('videos')}>
          Videos
        </button>
        <button className={`tab-button ${activeTab === 'docs' ? 'active' : ''}`} onClick={() => setActiveTab('docs')}>
          Docs
        </button>
      </div>

      {/* Wallet Modal */}
      {showWalletModal && (
        <div className="wallet-modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <h3>🔐 Manage Wallets</h3>
              <button className="close-modal" onClick={() => setShowWalletModal(false)}>×</button>
            </div>
            <div className="wallet-modal-content">
              {userWallets.length === 0 ? (
                <div className="no-wallets">
                  <p>No wallets saved yet. Add a wallet to get started.</p>
                </div>
              ) : (
                <div className="wallet-list">
                  {userWallets.map(wallet => (
                    <div
                      key={wallet.id}
                      className={`wallet-item ${selectedWallet === wallet.wallet_address ? 'selected' : ''}`}
                    >
                      <div className="wallet-info">
                        <input
                          type="radio"
                          name="selectedWallet"
                          checked={selectedWallet === wallet.wallet_address}
                          onChange={() => setSelectedWallet(wallet.wallet_address)}
                        />
                        <span className="wallet-address">
                          {wallet.wallet_address.slice(0, 8)}...{wallet.wallet_address.slice(-8)}
                        </span>
                        {wallet.is_primary && <span className="primary-badge">Primary</span>}
                      </div>
                      <div className="wallet-actions">
                        {!wallet.is_primary && (
                          <button
                            onClick={() => setPrimaryWallet(wallet.wallet_address)}
                            className="btn-set-primary"
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          onClick={() => deleteWallet(wallet.id)}
                          className="btn-delete-wallet"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setShowAddWallet(true)} className="btn-add-wallet">
                + Add New Wallet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Wallet Modal */}
      {showAddWallet && (
        <div className="add-wallet-modal-overlay" onClick={() => setShowAddWallet(false)}>
          <div className="add-wallet-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Add Solana Wallet</h4>
            <input
              type="text"
              placeholder="Enter Solana wallet address (32-44 characters)"
              value={newWalletAddress}
              onChange={(e) => setNewWalletAddress(e.target.value)}
              className="wallet-address-input"
            />
            <div className="modal-actions">
              <button onClick={addWallet} className="btn-confirm">Add Wallet</button>
              <button
                onClick={() => {
                  setShowAddWallet(false);
                  setNewWalletAddress('');
                }}
                className="btn-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'swap' && renderSwapTab()}
        {activeTab === 'alphascan' && renderAlphaScanTab()}
        {activeTab === 'positions' && renderPositionsTab()}
        {activeTab === 'portfolio' && renderPortfolioTab()}
        {activeTab === 'videos' && renderVideosTab()}
        {activeTab === 'docs' && renderDocsTab()}

      {/* Expanded Chart Modal with Tabs */}
      {expandedChart && (
        <div className="chart-modal-overlay" onClick={() => setExpandedChart(null)}>
          <div className="chart-modal-jupiter" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h3>
                {expandedChart === 'input' ? inputToken : outputToken} / USD
              </h3>
              <button className="close-modal" onClick={() => setExpandedChart(null)}>×</button>
            </div>

            <div className="jupiter-layout">
  {/* Top Row - Chart on left, Swap on right */}
  <div className="top-section">
    <div className="chart-section">
      <iframe
        src={`https://dexscreener.com/solana/${
          expandedChart === 'input' 
            ? (STABLECOINS[inputToken]?.mint || 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v')
            : (tokenSearchResults.find(t => t.symbol === outputToken)?.address || 'So11111111111111111111111111111111111111112')
        }?embed=1&theme=dark&trades=0&info=0`}
        style={{width: '100%', height: '100%', border: 'none'}}
        title={`${expandedChart === 'input' ? inputToken : outputToken} Chart`}
      />
    </div>
    
    <div className="swap-panel">
      <div className="swap-panel-header">
        <h4>Quick Swap</h4>
      </div>
      
      <div className="swap-panel-content">
        {/* Sell Section */}
        <div className="panel-input-section">
          <label>Sell</label>
          <select
            value={inputToken}
            onChange={(e) => {
              setInputToken(e.target.value);
              setQuote(null);
            }}
            className="panel-token-select"
          >
            {Object.keys(STABLECOINS).map(symbol => (
              <option key={symbol} value={symbol}>{symbol}</option>
            ))}
          </select>
          <input
            type="number"
            placeholder="0.00"
            value={inputAmount}
            onChange={(e) => {
              setInputAmount(e.target.value);
              setQuote(null);
            }}
            className="panel-amount-input"
          />
        </div>

        {/* Swap Icon */}
        <div className="panel-swap-icon">
          <button onClick={flipTokens}>⇅</button>
        </div>

        {/* Buy Section */}
        <div className="panel-input-section">
          <label>Buy</label>
          <input
            type="text"
            placeholder="Search token..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="panel-token-search"
          />
          {tokenSearchResults.length > 0 && (
            <div className="panel-search-results">
              {tokenSearchResults.slice(0, 5).map(token => (
                <div
                  key={token.address}
                  onClick={() => {
                    setOutputToken(token.symbol);
                    setSearchQuery(token.symbol);
                    setTokenSearchResults([]);
                    setQuote(null);
                  }}
                  className="panel-token-item"
                >
                  {token.logoURI && <img src={token.logoURI} alt={token.symbol} />}
                  <span>{token.symbol}</span>
                </div>
              ))}
            </div>
          )}
          <input
            type="text"
            placeholder="0.00"
            value={outputAmount}
            readOnly
            className="panel-amount-input"
          />
        </div>

        {/* Slippage */}
        <div className="panel-slippage">
          <label>Slippage: {slippage}%</label>
          <input
            type="range"
            min="0.1"
            max="5"
            step="0.1"
            value={slippage}
            onChange={(e) => setSlippage(parseFloat(e.target.value))}
          />
        </div>

        {/* Action Buttons */}
        <button
          onClick={getQuote}
          disabled={loading || !inputAmount || !inputToken || !outputToken || !selectedWallet}
          className="panel-get-quote-btn"
        >
          {loading ? 'Loading...' : !selectedWallet ? 'Connect Wallet' : 'Get Quote'}
        </button>

        {quote && (
          <>
            <div className="panel-quote-details">
              <div className="quote-item">
                <span>Rate:</span>
                <span>1 {quote.inputTokenData.symbol} = {(parseFloat(outputAmount) / parseFloat(inputAmount)).toFixed(6)} {quote.outputTokenData.symbol}</span>
              </div>
              <div className="quote-item">
                <span>Price Impact:</span>
                <span>{quote.priceImpactPct ? quote.priceImpactPct.toFixed(2) : 'N/A'}%</span>
              </div>
              <div className="quote-item">
                <span>Commission:</span>
                <span>${(parseFloat(inputAmount) * (commissionConfig?.commission_percent || 0.5) / 100).toFixed(2)}</span>
              </div>
            </div>
            
            <button
              onClick={executeSwap}
              disabled={loading}
              className="panel-execute-btn"
            >
              {loading ? 'Executing...' : 'Execute Swap'}
            </button>
          </>
        )}
      </div>
    </div>
  </div>

  {/* Bottom Row - Data Tabs spanning full width */}
  <div className="bottom-section">
    <ChartDataTabs
      tokenAddress={
        expandedChart === 'input' 
          ? (STABLECOINS[inputToken]?.mint || 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v')
          : (tokenSearchResults.find(t => t.symbol === outputToken)?.address || 'So11111111111111111111111111111111111111112')
      }
      tokenSymbol={expandedChart === 'input' ? inputToken : outputToken}
      activeTab={chartModalTab}
      setActiveTab={setChartModalTab}
	  heliusApiKey={HELIUS_API_KEY}
    />
  </div>
</div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};


export default MemeCoins;
